/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, useRef } from 'preact/hooks';
import { Keys, containsKey, addKey, removeKey, addKeys, removeKeys } from '#utils/UNSAFE_keys';
import { getIsSelectionPending } from '#utils/PRIVATE_textSelectionUtils';
import { SelectionDetail, SelectionMode } from '#UNSAFE_Collection';
import { SelectionRangeDetail } from './Selection.types';

/**
 * The hook for handling selection for collection ListView that is used as itemRenderer inside SelectSingle/SelectMultiple.
 *
 * @param keyExtractor function to extract the key based on provided element
 * @param selectedKeys the currently selected keys
 * @param selectionMode the selection mode
 * @param isSelectionRequired true if empty selected key is not allowed, false otherwise
 * @param getIndexByKey function to get the index based on the given key
 * @param getKeysByRange function to get the keys based on the given startIndex and endIndex
 * @param getPrevNextKey function to get the previous/next key in the vertical direction based on the current key
 * @param currentKey the current key
 * @param onChange callback function that is invoked when selection has changed.
 * @param onRangeChange callback function that is invoked when the selection range has changed.
 */
export function useAdditiveSelection<K>(
  keyExtractor: (element: HTMLElement) => K | null,
  selectedKeys: Keys<K>,
  selectionMode: SelectionMode,
  isSelectionRequired: boolean,
  getIndexByKey: (key: K) => number,
  getKeysByRange: (startIndex: number, endIndex: number) => K[],
  getPrevNextKey: (currentKey: K | undefined, isPrev: boolean) => () => K | null,
  currentKey?: K,
  onChange?: (detail: SelectionDetail<K>) => void,
  onRangeChange?: (detail: SelectionRangeDetail<K>) => void,
  allowEnterKey = true
) {
  // the start key of the range selection
  const anchorKey = useRef<K>();
  // the previous end key of the range selection, i.e. the last key during shift+click/arrow keys
  const cursorKey = useRef<K>();

  // shift+click on checkbox vs item behave differently, use a pending start key to help
  const pendingAnchorKey = useRef<K>();

  // reset anchorKey and cursorKey when a new range happen (e.g. after arrow key, click on item/selector)
  const setAnchorAndCursorKey = useCallback((key: K | undefined) => {
    anchorKey.current = key;
    cursorKey.current = undefined;
    pendingAnchorKey.current = undefined;
  }, []);

  // need to reset anchorKey and cursorKey during capture phase, because Selector doesn't propagate click event
  const onClickCapture = useCallback(
    (event: MouseEvent) => {
      if (selectionMode === 'multiple') {
        const itemKey = preClickProcessing(event, keyExtractor);
        if (itemKey === undefined) return;

        if (!event.shiftKey || anchorKey.current === undefined) {
          // reset anchorKey and cursorKey, a new range could happen after the click
          setAnchorAndCursorKey(itemKey);
        } else {
          // shift+click on item should not update anchor key, shift+click on checkbox should update anchor key
          if (pendingAnchorKey.current !== undefined) {
            setAnchorAndCursorKey(pendingAnchorKey.current);
          }
          pendingAnchorKey.current = itemKey;
        }
      }
    },
    [keyExtractor, selectionMode, setAnchorAndCursorKey]
  );

  const onClick = useCallback(
    (event: MouseEvent) => {
      const itemKey = preClickProcessing(event, keyExtractor);
      if (itemKey === undefined) return;

      if (selectionMode === 'single') {
        toggleSelection(event, selectedKeys, selectionMode, isSelectionRequired, itemKey, onChange);
      } else if (selectionMode === 'multiple') {
        // when anchorKey is undefined, i.e. shift+click is the first interaction, we should do toggle selection instead
        if (!event.shiftKey || anchorKey.current === undefined) {
          toggleSelection(
            event,
            selectedKeys,
            selectionMode,
            isSelectionRequired,
            itemKey,
            onChange
          );
        } else {
          // shift+click on item should not update anchor key, clear the pending anchor key here
          pendingAnchorKey.current = undefined;
          const newCursorKey = rangeSelection(
            event,
            selectedKeys,
            isSelectionRequired,
            anchorKey.current,
            cursorKey.current,
            itemKey,
            getIndexByKey,
            getKeysByRange,
            onChange,
            onRangeChange
          );

          if (newCursorKey !== undefined) {
            cursorKey.current = itemKey;
          }
        }
      }
    },
    [
      keyExtractor,
      selectedKeys,
      selectionMode,
      isSelectionRequired,
      getIndexByKey,
      getKeysByRange,
      onChange,
      onRangeChange
    ]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // prevent default (propagation) for keyDown to space bar causing container to scroll
      if (event.key === ' ') {
        event.preventDefault();
        return;
      }

      if (selectionMode === 'multiple') {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
          return;
        }

        // check ctrl/meta keys to avoid conflict gesture with reorder, and reset the range
        if (event.ctrlKey || event.metaKey) {
          setAnchorAndCursorKey(currentKey);
          return;
        }

        const itemKey = getPrevNextKey(currentKey, event.key === 'ArrowUp')();
        if (itemKey == null) {
          return;
        }

        if (!event.shiftKey) {
          // reset anchorKey and cursorKey, a new range could happen after the arrow keys
          setAnchorAndCursorKey(itemKey);
        } else {
          // Avoid increasing range of selection text selection with arrow keys
          event.preventDefault();

          if (pendingAnchorKey.current !== undefined) {
            setAnchorAndCursorKey(pendingAnchorKey.current);
          }

          // when anchorKey is undefined, i.e. shift+arrow was the first interaction
          if (anchorKey.current === undefined) {
            anchorKey.current = currentKey;
          }

          const newCursorKey = rangeSelection(
            event,
            selectedKeys,
            isSelectionRequired,
            anchorKey.current,
            cursorKey.current,
            itemKey,
            getIndexByKey,
            getKeysByRange,
            onChange,
            onRangeChange
          );

          if (newCursorKey !== undefined) {
            cursorKey.current = newCursorKey;
          }
        }
      }
    },
    [
      getPrevNextKey,
      currentKey,
      selectedKeys,
      selectionMode,
      isSelectionRequired,
      setAnchorAndCursorKey,
      getIndexByKey,
      getKeysByRange,
      onChange,
      onRangeChange
    ]
  );

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === ' ' || (allowEnterKey && event.key === 'Enter')) {
        const itemKey = keyExtractor(event.target as HTMLElement);
        if (itemKey == null) {
          return;
        }

        toggleSelection(event, selectedKeys, selectionMode, isSelectionRequired, itemKey, onChange);
      }
    },
    [keyExtractor, allowEnterKey, selectedKeys, selectionMode, isSelectionRequired, onChange]
  );

  const onPointerDown = useCallback(
    (event: PointerEvent) => {
      if (anchorKey.current !== undefined && event.shiftKey && selectionMode === 'multiple') {
        // Avoid text selection during shift+click, unless when anchorKey is undefined, i.e. shift+click
        // is the first interaction, which should do toggle selection and update currentKey during initial focus
        event.preventDefault();
      }
    },
    [selectionMode]
  );

  const additiveSelectionProps =
    selectionMode === 'none' || onChange == null
      ? {}
      : { onClickCapture, onClick, onKeyDown, onKeyUp, onPointerDown };
  return {
    eventProps: additiveSelectionProps
  };
}

/**
 * The helper function to determine whether we should process the click or click capture event.
 * Only process when there is a valid itemKey. Return the itemKey in this case.
 * @param event
 * @param keyExtractor
 */
const preClickProcessing = <K>(
  event: MouseEvent,
  keyExtractor: (element: HTMLElement) => K | null
) => {
  if (getIsSelectionPending()) {
    return undefined;
  }

  const itemKey = keyExtractor(event.target as HTMLElement);
  if (itemKey == null) {
    return undefined;
  }

  return itemKey;
};

/**
 * The helper function for range selection
 */
const rangeSelection = <K>(
  event: MouseEvent | KeyboardEvent,
  selectedKeys: Keys<K>,
  isSelectionRequired: boolean,
  anchorKey: K | undefined,
  cursorKey: K | undefined,
  itemKey: K,
  getIndexByKey: (key: K) => number,
  getKeysByRange: (startIndex: number, endIndex: number) => K[],
  onChange?: (detail: SelectionDetail<K>) => void,
  onRangeChange?: (detail: SelectionRangeDetail<K>) => void
) => {
  if (anchorKey !== undefined) {
    if (cursorKey !== undefined && cursorKey !== itemKey) {
      let keySet = selectedKeys;

      const startIndex = getIndexByKey(anchorKey);
      const endIndex = getIndexByKey(itemKey);
      const previousEndIndex = getIndexByKey(cursorKey);
      if (startIndex === -1 || endIndex === -1 || previousEndIndex === -1) {
        return undefined;
      }

      const minIndex = Math.min(startIndex, endIndex);
      const maxIndex = Math.max(startIndex, endIndex);

      // add keys that are within the range
      const keys = getKeysByRange(minIndex, maxIndex + 1);
      keySet = addKeys(keySet, keys);

      // only remove keys when previousEndIndex is out of range
      if (previousEndIndex < minIndex || previousEndIndex > maxIndex) {
        const keys =
          previousEndIndex < minIndex
            ? getKeysByRange(previousEndIndex, minIndex)
            : getKeysByRange(maxIndex + 1, previousEndIndex + 1);
        keySet = removeKeys(keySet, keys, isSelectionRequired);
      }

      if (selectedKeys != keySet && onChange) {
        onChange({ value: keySet, target: event.target });
        if (event.type === 'click') {
          event.stopPropagation();
        }
      }
    } else {
      // if no cursorKey was set, meaning the previous interaction was not shift+click/arrow,
      // we can do range selection directly
      onRangeChange?.({
        value: { start: anchorKey, end: itemKey },
        eventType: event.type === 'click' ? 'shiftClick' : 'shiftArrowKeys'
      });
    }
    return itemKey;
  }

  return undefined;
};

const toggleSelection = <K>(
  event: MouseEvent | KeyboardEvent,
  selectedKeys: Keys<K>,
  selectionMode: SelectionMode,
  isSelectionRequired: boolean,
  itemKey: K,
  onChange?: (detail: SelectionDetail<K>) => void
) => {
  let keySet = selectedKeys;
  const selected = containsKey(keySet, itemKey);
  if (!selected) {
    if (selectionMode === 'single') {
      keySet = { all: false, keys: new Set([itemKey]) };
    } else {
      keySet = addKey(keySet, itemKey);
    }
  } else {
    keySet = removeKey(keySet, itemKey, isSelectionRequired);
  }

  if (selectedKeys != keySet && onChange) {
    onChange({ value: keySet, target: event.target });
    if (event.type === 'click') {
      event.stopPropagation();
    }
  }
};
